/** Storage reconciliation ("đối soát") — billing-basis summary + per-bucket rail. */
import { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  serviceApiV3,
  unwrap,
  storageBase,
  s3Keys,
  type StorageBusiness,
} from './_shared';

const EMPTY_SUMMARY: StorageBusiness['summary'] = {
  avg_stored_bytes: 0,
  peak_stored_bytes: 0,
  raw_bytes: 0,
  deleted_bytes: 0,
  metadata_bytes: 0,
  billable_objects: 0,
  gib_month: 0,
  points: 0,
};

/**
 * Windowed storage reconciliation for one service. `range` is an absolute
 * from/to (ISO-8601); the backend reads the console's retained daily history and
 * folds it to the billing-basis summary + per-bucket breakdown. Non-Wasabi
 * services answer an empty summary/buckets (rendered as empty state).
 *
 * The backend caches reads (8h TTL, per service+window). The returned `refresh()`
 * forces one fresh vendor pull by sending `refresh=1` (which bypasses that
 * server cache) on the next refetch — normal loads stay cached.
 */
export function useStorageBusiness(
  serviceId: string | undefined,
  range: { from: string; to: string },
) {
  // One-shot cache bust: set just before a manual refetch, consumed by the
  // queryFn so background refetches don't keep hammering the vendor.
  const bust = useRef(false);

  const query = useQuery<StorageBusiness>({
    queryKey: s3Keys.business(serviceId ?? '', range.from, range.to),
    enabled: !!serviceId,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const params: Record<string, string | number> = { from: range.from, to: range.to };
      if (bust.current) {
        params.refresh = 1;
        bust.current = false;
      }
      const res = await serviceApiV3.get(`${storageBase(serviceId!)}/business`, { params });
      const data = unwrap<StorageBusiness>(res);
      return {
        from: data?.from ?? range.from,
        to: data?.to ?? range.to,
        summary: { ...EMPTY_SUMMARY, ...(data?.summary ?? {}) },
        buckets: data?.buckets ?? [],
      };
    },
  });

  const refresh = () => {
    bust.current = true;
    void query.refetch();
  };

  return { ...query, refresh };
}
